/**
 * ResolutionRequestValidator (AJIP-16.02.006 · WS2)
 * ---------------------------------------------------------------------
 * Strict, deterministic validation for a Resolution Request. Every
 * check is fail-closed. No invalid request reaches Resolution
 * evaluation.
 *
 * The validator is provider-agnostic and framework-agnostic. It never
 * inspects storage, never makes a network call, and never reads env.
 */
import {
  KNOWN_REQUEST_FIELDS,
  REQUIRED_REQUEST_FIELDS,
  FORBIDDEN_REQUEST_KEYS,
  ENTRY_POINTS,
  MAX_REQUEST_BYTES,
} from './ResolutionRequestContract';
import { validateDiscoveryContext } from '../registrationDiscovery/DiscoveryContext';
import { InvalidResolutionRequestError } from './ResolutionActivationErrors';

const forbiddenLc = new Set(FORBIDDEN_REQUEST_KEYS.map((k) => k.toLowerCase()));
const isNonEmptyString = (v) => typeof v === 'string' && v.trim().length > 0;
const isStringArray = (v) => Array.isArray(v) && v.every((x) => typeof x === 'string');
const isPlainObject = (v) => (
  v !== null && typeof v === 'object' && !Array.isArray(v)
  && (Object.getPrototypeOf(v) === Object.prototype || Object.getPrototypeOf(v) === null)
);

const walkForbidden = (value, path = 'request') => {
  if (value === null || value === undefined || typeof value !== 'object') return;
  if (typeof value === 'function') {
    throw new InvalidResolutionRequestError(`Executable data at ${path} is prohibited`);
  }
  if (Array.isArray(value)) {
    value.forEach((item, i) => walkForbidden(item, `${path}[${i}]`));
    return;
  }
  if (!isPlainObject(value)) {
    throw new InvalidResolutionRequestError(`${path} must be a plain object`);
  }
  for (const [k, v] of Object.entries(value)) {
    if (forbiddenLc.has(k.toLowerCase())) {
      throw new InvalidResolutionRequestError(`Forbidden key '${k}' at ${path}`);
    }
    if (typeof v === 'function') {
      throw new InvalidResolutionRequestError(`Executable data at ${path}.${k} is prohibited`);
    }
    walkForbidden(v, `${path}.${k}`);
  }
};

const _canonicalFlagsFallback = () => new Set([
  'JOURNEYS_ENABLED', 'JOURNEYS_AI', 'JOURNEYS_MATCH',
  'JOURNEYS_PERSISTENCE', 'JOURNEYS_ANALYTICS', 'JOURNEYS_RECOMMENDATIONS',
  'JOURNEYS_FACTORY',
]);

const validateResolutionRequest = (
  request,
  { knownFlags = null, knownEntryPoints = ENTRY_POINTS } = {},
) => {
  if (!isPlainObject(request)) {
    throw new InvalidResolutionRequestError('Request must be a plain object');
  }
  walkForbidden(request);

  // Payload cap.
  let serialised;
  try { serialised = JSON.stringify(request); } catch (_e) {
    throw new InvalidResolutionRequestError('Request is not serialisable');
  }
  const byteLen = (typeof Buffer !== 'undefined')
    ? Buffer.byteLength(serialised, 'utf8')
    : serialised.length; // pure-browser fallback
  if (byteLen > MAX_REQUEST_BYTES) {
    throw new InvalidResolutionRequestError(`Request exceeds ${MAX_REQUEST_BYTES} bytes`);
  }

  // Unknown top-level keys are rejected. Preserves an evolvable but
  // strict surface.
  for (const key of Object.keys(request)) {
    if (!KNOWN_REQUEST_FIELDS.includes(key)) {
      throw new InvalidResolutionRequestError(`Unknown request field '${key}'`);
    }
  }

  // Required fields.
  for (const f of REQUIRED_REQUEST_FIELDS) {
    if (!(f in request)) {
      throw new InvalidResolutionRequestError(`Missing required field: ${f}`);
    }
  }

  // requestId
  if (!isNonEmptyString(request.requestId)) {
    throw new InvalidResolutionRequestError('requestId must be a non-empty string');
  }

  // journey targeting — at least one identifier hint is required to
  // resolve. journeyId is preferred; journeyType is acceptable.
  if (!('journeyId' in request) && !('journeyType' in request)) {
    throw new InvalidResolutionRequestError('Either journeyId or journeyType must be supplied');
  }
  if ('journeyId' in request && !isNonEmptyString(request.journeyId)) {
    throw new InvalidResolutionRequestError('journeyId must be non-empty string');
  }
  if ('journeyType' in request && !isNonEmptyString(request.journeyType)) {
    throw new InvalidResolutionRequestError('journeyType must be non-empty string');
  }
  if ('journeyVersion' in request) {
    if (!Number.isInteger(request.journeyVersion) || request.journeyVersion < 1) {
      throw new InvalidResolutionRequestError('journeyVersion must be integer >= 1');
    }
  }

  // principalContext
  if (!isPlainObject(request.principalContext)) {
    throw new InvalidResolutionRequestError('principalContext must be a plain object');
  }
  if ('principalRef' in request.principalContext) {
    const pr = request.principalContext.principalRef;
    if (pr !== null && !isNonEmptyString(pr)) {
      throw new InvalidResolutionRequestError('principalContext.principalRef must be non-empty string or null');
    }
  }

  // discoveryContext — delegate to the existing validator (already
  // rejects prohibited keys and enforces plain-object shape).
  validateDiscoveryContext(request.discoveryContext);

  // entryPoint
  if (!knownEntryPoints.includes(request.entryPoint)) {
    throw new InvalidResolutionRequestError(`Unknown entryPoint '${request.entryPoint}'`);
  }

  // requiredFeatureFlags
  const flags = knownFlags instanceof Set ? knownFlags : _canonicalFlagsFallback();
  if (!isStringArray(request.requiredFeatureFlags)) {
    throw new InvalidResolutionRequestError('requiredFeatureFlags must be string array');
  }
  for (const f of request.requiredFeatureFlags) {
    if (!flags.has(f)) {
      throw new InvalidResolutionRequestError(`Unknown feature flag '${f}'`);
    }
  }

  // activationIntent — must be explicit boolean
  if (typeof request.activationIntent !== 'boolean') {
    throw new InvalidResolutionRequestError('activationIntent must be an explicit boolean');
  }

  // resumeIntent — optional, if present must be boolean; if true then
  // existingInstanceRef must be present.
  if ('resumeIntent' in request) {
    if (typeof request.resumeIntent !== 'boolean') {
      throw new InvalidResolutionRequestError('resumeIntent must be boolean');
    }
    if (request.resumeIntent === true && !isNonEmptyString(request.existingInstanceRef)) {
      throw new InvalidResolutionRequestError('resumeIntent=true requires existingInstanceRef');
    }
  }
  if ('existingInstanceRef' in request && request.existingInstanceRef !== null
      && !isNonEmptyString(request.existingInstanceRef)) {
    throw new InvalidResolutionRequestError('existingInstanceRef must be non-empty string or null');
  }

  // Optional ISO timestamps.
  if ('requestedAt' in request && request.requestedAt !== null) {
    if (typeof request.requestedAt !== 'string' || Number.isNaN(Date.parse(request.requestedAt))) {
      throw new InvalidResolutionRequestError('requestedAt must be ISO string or null');
    }
  }
  if ('correlationRef' in request && request.correlationRef !== null
      && !isNonEmptyString(request.correlationRef)) {
    throw new InvalidResolutionRequestError('correlationRef must be non-empty string or null');
  }
  if ('canonReference' in request && request.canonReference !== null
      && !isNonEmptyString(request.canonReference)) {
    throw new InvalidResolutionRequestError('canonReference must be non-empty string or null');
  }
  if ('metadata' in request && !isPlainObject(request.metadata)) {
    throw new InvalidResolutionRequestError('metadata must be plain object');
  }

  return true;
};

export { validateResolutionRequest };
